import { storedSettingsRevision, copyRevisionSiblings } from "./roofingV2RevisionView";
import { derivedModeAccepted } from "./roofingV2ModeReasons";
import { activationValueText } from "./roofingV2QualificationView";

function phraseMap(value) {
  const source = value && typeof value === "object" ? value : {};
  const out = {};
  for (const key of Object.keys(source)) {
    if (key.trim() === "") {
      return null;
    }
    out[key] = source[key] == null ? "" : String(source[key]);
  }
  return out;
}

function comparablePhraseMap(value) {
  const source = value && typeof value === "object" ? value : {};
  const out = {};
  for (const key of Object.keys(source)) {
    if (key.trim() === "") {
      continue;
    }
    out[key] = source[key] == null ? "" : String(source[key]);
  }
  return out;
}

function blankPhraseEntries(value) {
  const source = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const out = {};
  for (const key of Object.keys(source)) {
    if (key.trim() === "") {
      out[key] = source[key] == null ? "" : String(source[key]);
    }
  }
  return out;
}

function omittedStoredBlankKeys(settings, draft) {
  const stored = blankPhraseEntries(settings && settings.phrase_settings);
  const marked = draft && Array.isArray(draft.omittedBlankKeys) ? draft.omittedBlankKeys : [];
  return marked.filter((key) => Object.prototype.hasOwnProperty.call(stored, key));
}

function displayPhraseMap(value) {
  return { ...comparablePhraseMap(value), ...blankPhraseEntries(value) };
}

export function phraseRows(phraseSettings) {
  const map = comparablePhraseMap(phraseSettings);
  return Object.keys(map).map((key) => ({ key, value: map[key] }));
}

function sameMap(left, right) {
  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  if (leftKeys.length !== rightKeys.length) {
    return false;
  }
  for (let index = 0; index < leftKeys.length; index += 1) {
    const key = leftKeys[index];
    if (key !== rightKeys[index] || left[key] !== right[key]) {
      return false;
    }
  }
  return true;
}

export function buildMetadataSaveBody({ settings, draft, commandId }) {
  const brand = draft.brand == null ? "" : String(draft.brand);
  const tone = draft.tone == null ? "" : String(draft.tone);
  if (brand.trim() === "" || tone.trim() === "") {
    return { error: "empty" };
  }
  const phrases = phraseMap(draft.phrase_settings);
  if (!phrases) {
    return { error: "empty" };
  }
  const overlay = {};
  if (brand !== settings.brand) {
    overlay.brand = brand;
  }
  if (tone !== settings.tone) {
    overlay.tone = tone;
  }
  const storedBlank = blankPhraseEntries(settings && settings.phrase_settings);
  const omitted = new Set(omittedStoredBlankKeys(settings, draft));
  const keptBlank = {};
  for (const key of Object.keys(storedBlank)) {
    if (!omitted.has(key)) {
      keptBlank[key] = storedBlank[key];
    }
  }
  if (!sameMap(phrases, comparablePhraseMap(settings && settings.phrase_settings)) || omitted.size > 0) {
    overlay.phrase_settings = {
      ...keptBlank,
      ...phrases,
    };
  }
  if (Object.keys(overlay).length === 0) {
    return null;
  }
  if (!commandId) {
    return { error: "empty" };
  }
  const revision = storedSettingsRevision(settings && settings.settings_revision);
  if (revision === null) {
    return { error: "revision" };
  }
  const schemaVersion = storedSettingsRevision(settings && settings.schema_version);
  if (schemaVersion === null) {
    return { error: "schema" };
  }
  return {
    command_id: commandId,
    schema_version: schemaVersion,
    expected_settings_revision: revision,
    overlay,
  };
}

export function metadataChangeNames(settings, draft) {
  const stored = settings || {};
  const names = [];
  const brand = draft.brand == null ? "" : String(draft.brand);
  const tone = draft.tone == null ? "" : String(draft.tone);
  if (brand !== stored.brand) {
    names.push("brand");
  }
  if (tone !== stored.tone) {
    names.push("tone");
  }
  const phrases = comparablePhraseMap(draft.phrase_settings);
  const storedPhrases = comparablePhraseMap(stored.phrase_settings);
  const blankPhrases = blankPhraseEntries(draft.phrase_settings);
  const storedBlankPhrases = blankPhraseEntries(stored.phrase_settings);
  const storedBlankInDraft = {};
  for (const key of Object.keys(blankPhrases)) {
    if (Object.prototype.hasOwnProperty.call(storedBlankPhrases, key)) {
      storedBlankInDraft[key] = storedBlankPhrases[key];
    }
  }
  if (!sameMap(phrases, storedPhrases) || !sameMap(blankPhrases, storedBlankInDraft)) {
    names.push("phrase_settings");
  }
  return names;
}

export function collapsedPhraseSettings(rows) {
  const phraseSettings = {};
  const seen = new Set();
  const repeatedKeys = new Set();
  const list = Array.isArray(rows) ? rows : [];
  for (const row of list) {
    const key = row && row.key;
    if (seen.has(key)) {
      repeatedKeys.add(key);
    }
    seen.add(key);
    phraseSettings[key] = row && row.value;
  }
  return { phraseSettings, repeated: repeatedKeys.size > 0, repeatedKeys };
}

export function draftChangeNames(settings, draft) {
  const collapsed = collapsedPhraseSettings(draft && draft.rows);
  const names = metadataChangeNames(settings, {
    brand: draft && draft.brand,
    tone: draft && draft.tone,
    phrase_settings: collapsed.phraseSettings,
  });
  if (collapsed.repeated && !names.includes("phrase_settings")) {
    names.push("phrase_settings");
  }
  if (omittedStoredBlankKeys(settings, draft).length > 0 && !names.includes("phrase_settings")) {
    names.push("phrase_settings");
  }
  return names;
}

export function metadataChangeDetails(settings, draft) {
  const stored = settings || {};
  const details = [];
  const brand = draft.brand == null ? "" : String(draft.brand);
  const tone = draft.tone == null ? "" : String(draft.tone);
  if (brand !== stored.brand) {
    details.push({
      name: "brand",
      from: stored.brand == null ? "" : String(stored.brand),
      to: brand,
      change: "changed",
    });
  }
  if (tone !== stored.tone) {
    details.push({
      name: "tone",
      from: stored.tone == null ? "" : String(stored.tone),
      to: tone,
      change: "changed",
    });
  }
  const phrases = displayPhraseMap(draft.phrase_settings);
  const storedPhrases = displayPhraseMap(stored.phrase_settings);
  const keys = Object.keys(phrases).concat(Object.keys(storedPhrases));
  keys.sort();
  const seen = new Set();
  for (const key of keys) {
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    const hasFrom = Object.prototype.hasOwnProperty.call(storedPhrases, key);
    const hasTo = Object.prototype.hasOwnProperty.call(phrases, key);
    if (key.trim() === "" && !hasTo) {
      continue;
    }
    const from = hasFrom ? storedPhrases[key] : "";
    const to = hasTo ? phrases[key] : "";
    if (!hasFrom || !hasTo || from !== to) {
      let change = "changed";
      if (!hasFrom) {
        change = "added";
      } else if (!hasTo) {
        change = "removed";
      }
      details.push({ name: key, from, to, change });
    }
  }
  return details;
}

function changeValueText(name, value) {
  const text = value == null ? "" : String(value);
  if ((name === "brand" || name === "tone") && text.trim() === "") {
    return '""';
  }
  return activationValueText(value);
}

function changeNameText(name) {
  const text = name == null ? "" : String(name);
  if (text.trim() === "") {
    return '""';
  }
  return text;
}

export function storedBlankPhraseEntries(phraseSettings, omittedKeys) {
  const omitted = new Set(Array.isArray(omittedKeys) ? omittedKeys : []);
  const entries = blankPhraseEntries(phraseSettings);
  return Object.keys(entries).sort().filter((key) => !omitted.has(key)).map((key) => ({
    key,
    line: `${changeNameText(key)}: ${activationValueText(entries[key])}`,
  }));
}

export function storedBlankPhraseLines(phraseSettings, omittedKeys) {
  return storedBlankPhraseEntries(phraseSettings, omittedKeys).map((entry) => entry.line);
}

export function storedBlankPhraseRemoveLabel(line) {
  return `Remove ${line}`;
}

export function metadataChangeLine(detail) {
  const name = changeNameText(detail.name);
  const from = changeValueText(detail.name, detail.from);
  const to = changeValueText(detail.name, detail.to);
  if (detail.change === "removed") {
    return `${name}: ${from} → removed`;
  }
  if (detail.change === "added") {
    return `${name}: added → ${to}`;
  }
  if (detail.change === "repeated") {
    return `${name}: repeated`;
  }
  return `${name}: ${from} → ${to}`;
}

export function draftChangeDetails(settings, draft) {
  const collapsed = collapsedPhraseSettings(draft && draft.rows);
  const details = metadataChangeDetails(settings, {
    brand: draft && draft.brand,
    tone: draft && draft.tone,
    phrase_settings: collapsed.phraseSettings,
  }).filter((detail) => {
    if (detail.change !== "removed") {
      return true;
    }
    return !collapsed.repeatedKeys.has(detail.name);
  });
  for (const key of collapsed.repeatedKeys) {
    details.push({ name: key, change: "repeated" });
  }
  const storedBlank = blankPhraseEntries(settings && settings.phrase_settings);
  for (const key of omittedStoredBlankKeys(settings, draft)) {
    details.push({ name: key, from: storedBlank[key], to: "", change: "removed" });
  }
  return details;
}

export function metadataDraftNeedsReset(settings, draft) {
  const stored = settings || {};
  const brand = draft.brand == null ? "" : String(draft.brand);
  const tone = draft.tone == null ? "" : String(draft.tone);
  if (brand !== stored.brand || tone !== stored.tone) {
    return true;
  }
  if (omittedStoredBlankKeys(settings, draft).length > 0) {
    return true;
  }
  const rows = Array.isArray(draft.rows) ? draft.rows : [];
  const fromRows = {};
  const seen = new Set();
  for (const row of rows) {
    const key = row && row.key == null ? "" : String(row.key);
    if (seen.has(key)) {
      return true;
    }
    seen.add(key);
    fromRows[key] = row && row.value == null ? "" : String(row.value);
  }
  return !sameMap(fromRows, comparablePhraseMap(stored.phrase_settings));
}

export function leavePageWarning(needsReset) {
  if (!needsReset) {
    return "";
  }
  return "Brand, tone, or phrases are not saved.";
}

export function applyMetadataSave(_snapshot, responseBody) {
  const derivedMode = derivedModeAccepted(responseBody && responseBody.derived_mode);
  const settingsSource = responseBody && responseBody.settings;
  if (!derivedMode || !settingsSource || settingsSource.bot_type !== "roofing_v2") {
    return { accepted: false };
  }
  const settings = copyRevisionSiblings(settingsSource, responseBody);
  delete settings.credential_refs;
  return {
    accepted: true,
    settings,
    derivedMode,
  };
}
